import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import React from 'react';

import logger from 'server/logger';
import { shouldRejectSlug, validateSlug } from 'utils/slugValidator';

import BasePage from 'components/BasePage/BasePage';
import { IContentPage } from 'models/page';
import { ContentPageService } from 'services/contentPageService';
import { fetcher } from 'services/httpRequestor';

type SlugPageProps = {
  page?: IContentPage;
  statusCode?: number;
};

/**
 * Catch-all route for dynamic pages (does not match root "/" which is handled by index.tsx)
 *
 * This route handles all dynamic page URLs including nested paths.
 *
 * Examples:
 * - /about-us -> slug = ['about-us']
 * - /about-us/our-vision -> slug = ['about-us', 'our-vision']
 * - /about-us/team/leadership -> slug = ['about-us', 'team', 'leadership']
 *
 * Note: Root path "/" is handled by pages/index.tsx
 */
export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const { slug, ...getParams } = query;

  // Reject if no slug, empty slug, or system paths
  if (shouldRejectSlug(slug)) {
    return {
      notFound: true,
    };
  }

  const slugPath = Array.isArray(slug) ? slug.join('/') : slug;
  const validation = validateSlug(slugPath);
  if (!validation.isValid) {
    logger.error(validation.error, 'Slug validation failed:');
    return {
      notFound: true,
    };
  }

  const pageService = new ContentPageService(fetcher(req.headers.cookie));
  try {
    const { data: page, status } = await pageService.getOneByPageName(
      slugPath,
      {
        query: getParams,
      },
    );
    logger.info({ page, status, slugPath, validation }, 'Page fetched by slug');

    if (status === 404) {
      return {
        props: {
          slug: slugPath,
          statusCode: 404,
        },
        notFound: true,
      };
    }
    // Pass data to the page via props
    return { props: { page } };
  } catch (error) {
    logger.error({ error, slugPath }, 'Failed to fetch page by slug');
    return {
      props: {
        statusCode: error.status,
      },
    };
  }
};

const SlugPage: React.FC<SlugPageProps> = ({
  page,
  statusCode,
  host,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  return <BasePage page={page} statusCode={statusCode} host={host} />;
};

export default SlugPage;
